import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import logoAve from '../assets/logos/logoave.png';
import utnFrcLogo from '../assets/logos/utnFrcLogo.png';
import AsistenteService from '../services/AsistenteService';
import materiaService from '../services/MateriaService';
import regionalApiService from '../services/RegionalApiService';
import publicAccessService from '../services/PublicAccessService';
import ChatModal from './common/ChatModal';
import PublicCaptchaGate from './common/PublicCaptchaGate';
import {
  getAssistantProcessingClassName,
  getAssistantProcessingDetail,
  getAssistantProcessingLabel,
  getAssistantProcessingPercent,
  isAssistantProcessingReady
} from '../utils/aveProcessing';
import './SubjectCardsGrid.css';
import './TestAve.css';

const buildSlug = (value) => {
  if (!value) return '';
  return value.trim().toLowerCase().replace(/\s+/g, '_');
};

const isCaptchaError = (error) => (error?.message || '').toLowerCase().includes('captcha');

const resolveAssistantId = (assistant) =>
  assistant?.idOpenAIAssistant ||
  assistant?.IdOpenAIAssistant ||
  assistant?.id ||
  assistant?.Id ||
  '';

const resolveAssistantName = (assistant) =>
  assistant?.nombre ||
  assistant?.Nombre ||
  assistant?.nombreAsistente ||
  assistant?.NombreAsistente ||
  'Asistente AVE';

const resolveSubjectName = (assistant) =>
  assistant?.nombreMateria ||
  assistant?.NombreMateria ||
  'Asistente institucional';

const normalizeSearchText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const TestAve = () => {
  const location = useLocation();
  const [isPublicAccessVerified, setIsPublicAccessVerified] = useState(publicAccessService.hasToken());
  const [regionalId, setRegionalId] = useState('');
  const [regionalName, setRegionalName] = useState('');
  const [regionalError, setRegionalError] = useState('');
  const [areaId, setAreaId] = useState('');
  const [areaName, setAreaName] = useState('');
  const [isResolvingRegional, setIsResolvingRegional] = useState(false);
  const [assistants, setAssistants] = useState([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [assistantsError, setAssistantsError] = useState('');
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [showConsent, setShowConsent] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isValidatingPassword, setIsValidatingPassword] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatAssistant, setChatAssistant] = useState(null);
  const [chatPassword, setChatPassword] = useState('');
  const [chatTitle, setChatTitle] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const resetPublicAccess = () => {
    publicAccessService.clearToken();
    setIsPublicAccessVerified(false);
    setShowConsent(false);
    setShowChatModal(false);
  };

  useEffect(() => {
    const handlePublicAccessExpired = () => resetPublicAccess();
    window.addEventListener('public-access-expired', handlePublicAccessExpired);
    return () => window.removeEventListener('public-access-expired', handlePublicAccessExpired);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idParam = params.get('regionalId') || params.get('idRegional') || '';
    const regionalParam = params.get('regional') || '';
    const areaParam = params.get('areaId') || params.get('idArea') || '';

    setRegionalError('');
    setAssistantsError('');
    setAssistants([]);
    setAreaId(areaParam);
    setAreaName('');

    if (!areaParam) {
      setRegionalId('');
      setRegionalError('Falta el área en la URL. Usá areaId o idArea.');
      return;
    }

    if (idParam) {
      setRegionalId(idParam);
      setRegionalName('');
      return;
    }

    if (!regionalParam) {
      setRegionalId('');
      setRegionalError('Falta el regional en la URL. Usá regionalId/idRegional o regional.');
      return;
    }

    const nombreUrl = buildSlug(regionalParam);
    setRegionalName(regionalParam);

    if (!isPublicAccessVerified) {
      setRegionalId('');
      return;
    }

    const resolveRegional = async () => {
      setIsResolvingRegional(true);
      try {
        const response = await regionalApiService.getRegionalByNombreUrl(nombreUrl);
        const list = response?.regionales || response?.Regionales || response?.data || response || [];
        const regional = Array.isArray(list) ? list[0] : list;
        const resolvedId = regional?.id || regional?.Id;
        if (!resolvedId) {
          throw new Error('No se encontró el regional solicitado.');
        }
        setRegionalId(resolvedId);
        setRegionalName(regional?.nombre || regional?.Nombre || regionalParam);
      } catch (error) {
        if (isCaptchaError(error)) {
          resetPublicAccess();
          return;
        }
        setRegionalId('');
        setRegionalError(error.message || 'No pudimos identificar el regional.');
      } finally {
        setIsResolvingRegional(false);
      }
    };

    resolveRegional();
  }, [location.search, isPublicAccessVerified]);

  useEffect(() => {
    const loadAssistants = async () => {
      if (!isPublicAccessVerified || !regionalId || !areaId) {
        return;
      }

      setLoadingAssistants(true);
      setAssistantsError('');
      try {
        const list = await AsistenteService.getAsistentesAvePublicos(regionalId, areaId);
        setAssistants(list);
        const firstWithArea = list.find((assistant) => assistant?.nombreArea || assistant?.NombreArea);
        setAreaName(firstWithArea?.nombreArea || firstWithArea?.NombreArea || '');
      } catch (error) {
        if (isCaptchaError(error)) {
          resetPublicAccess();
          return;
        }
        setAssistantsError(error.message || 'No pudimos cargar los asistentes AVE.');
      } finally {
        setLoadingAssistants(false);
      }
    };

    loadAssistants();
  }, [regionalId, areaId, isPublicAccessVerified]);

  const readyCount = useMemo(
    () => assistants.filter((assistant) => isAssistantProcessingReady(assistant)).length,
    [assistants]
  );

  const filteredAssistants = useMemo(() => {
    const query = normalizeSearchText(searchTerm);
    if (!query) return assistants;
    return assistants.filter((assistant) => normalizeSearchText([
      resolveAssistantName(assistant),
      assistant.descripcion || assistant.Descripcion
    ].join(' ')).includes(query));
  }, [assistants, searchTerm]);

  const handleOpenAssistant = (assistant) => {
    if (!isAssistantProcessingReady(assistant)) {
      setAssistantsError(getAssistantProcessingDetail(assistant) || 'El asistente AVE todavía no está listo para iniciar chat.');
      return;
    }

    setSelectedAssistant(assistant);
    setPasswordInput('');
    setPasswordError('');
    setShowConsent(true);
  };

  const handleCancelConsent = () => {
    setShowConsent(false);
    setSelectedAssistant(null);
    setPasswordInput('');
    setPasswordError('');
  };

  const handleConfirmConsent = async () => {
    if (!selectedAssistant) {
      handleCancelConsent();
      return;
    }

    const assistantId = resolveAssistantId(selectedAssistant);
    if (!assistantId) {
      setPasswordError('No se encontró el asistente seleccionado.');
      return;
    }

    const requiresPassword = selectedAssistant.requierePassword ?? selectedAssistant.RequierePassword ?? false;
    if (requiresPassword && !passwordInput.trim()) {
      setPasswordError('Ingresa el password para continuar.');
      return;
    }

    setIsValidatingPassword(true);
    setPasswordError('');
    try {
      if (requiresPassword) {
        const result = await materiaService.validateAsistentePassword(
          assistantId,
          regionalId,
          areaId,
          passwordInput.trim()
        );
        const valido = result?.valido ?? result?.Valido;
        if (!valido) {
          setPasswordError('El password ingresado no es correcto.');
          return;
        }
      }

      const assistantName = resolveAssistantName(selectedAssistant);
      setChatAssistant(selectedAssistant);
      setChatPassword(requiresPassword ? passwordInput.trim() : '');
      setChatTitle(`Asistente AVE ${assistantName}`);
      setShowConsent(false);
      setShowChatModal(true);
    } catch (error) {
      if (isCaptchaError(error)) {
        resetPublicAccess();
        return;
      }
      setPasswordError(error.message || 'No pudimos validar el acceso al asistente.');
    } finally {
      setIsValidatingPassword(false);
    }
  };

  const handleCloseChat = () => {
    setShowChatModal(false);
    setChatAssistant(null);
    setChatPassword('');
    setChatTitle('');
  };

  const showLoading = isResolvingRegional || loadingAssistants;
  const selectedAssistantName = selectedAssistant ? resolveAssistantName(selectedAssistant) : '';
  const selectedSubjectName = selectedAssistant ? resolveSubjectName(selectedAssistant) : '';
  const selectedRequiresPassword = selectedAssistant?.requierePassword ?? selectedAssistant?.RequierePassword ?? false;

  return (
    <div className="test-ave-page">
      {!isPublicAccessVerified && (
        <PublicCaptchaGate onVerified={() => setIsPublicAccessVerified(true)} />
      )}

      {showLoading && (
        <div className="test-ave-loading">
          <div className="test-ave-loading__card">
            <i className="fas fa-spinner fa-spin" />
            <span>Cargando asistentes AVE...</span>
          </div>
        </div>
      )}

      <header className="test-ave-header">
        <div className="test-ave-header__logos">
          <img src={utnFrcLogo} alt="UTN FRC" />
          <img src={logoAve} alt="AVE" />
        </div>
        <div className="test-ave-header__copy">
          <span>Prueba pública</span>
          <h1>Asistentes AVE - BETA</h1>
          <p>
            Seleccioná un asistente disponible, aceptá el consentimiento y probá el chat desde el flujo público.
          </p>
        </div>
        <div className="test-ave-header__meta">
          <strong>{areaName || 'Área seleccionada'}</strong>
          <span>{regionalName || 'Regional seleccionada'}</span>
        </div>
      </header>

      <main className="test-ave-main">
        <section className="test-ave-summary">
          <div>
            <strong>{assistants.length}</strong>
            <span>Asistentes AVE</span>
          </div>
          <div>
            <strong>{readyCount}</strong>
            <span>Listos para probar</span>
          </div>
          <div>
            <strong>{assistants.length - readyCount}</strong>
            <span>Pendientes o con error</span>
          </div>
        </section>

        {(regionalError || assistantsError) && (
          <div className="test-ave-alert">
            <i className="fas fa-triangle-exclamation" />
            <span>{regionalError || assistantsError}</span>
          </div>
        )}

        {!showLoading && !regionalError && !assistantsError && assistants.length === 0 && (
          <div className="test-ave-empty">
            <i className="fas fa-robot" />
            <h2>No hay asistentes AVE disponibles</h2>
            <p>No encontramos asistentes AVE activos para la regional y área indicadas.</p>
          </div>
        )}

        {assistants.length > 0 && (
          <div className="test-ave-search-row">
            <div className="test-ave-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar asistente por nombre o descripción"
                aria-label="Buscar asistentes"
              />
              {searchTerm && (
                <button type="button" onClick={() => setSearchTerm('')} title="Limpiar búsqueda">
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              )}
            </div>
            {searchTerm.trim() && (
              <span className="test-ave-search-count">
                {filteredAssistants.length} de {assistants.length} asistentes
              </span>
            )}
          </div>
        )}

        {assistants.length > 0 && filteredAssistants.length === 0 && (
          <div className="test-ave-empty">
            <i className="fas fa-search" />
            <h2>Sin resultados</h2>
            <p>No hay asistentes que coincidan con "{searchTerm.trim()}".</p>
          </div>
        )}

        <section className="test-ave-grid">
          {filteredAssistants.map((assistant) => {
            const assistantId = resolveAssistantId(assistant);
            const ready = isAssistantProcessingReady(assistant);
            const statusLabel = getAssistantProcessingLabel(assistant);
            const statusClassName = getAssistantProcessingClassName(assistant);
            const percent = getAssistantProcessingPercent(assistant);
            const detail = getAssistantProcessingDetail(assistant);
            const requiresPassword = assistant.requierePassword ?? assistant.RequierePassword ?? false;

            return (
              <article key={assistantId || resolveAssistantName(assistant)} className="test-ave-card">
                <div className="test-ave-card__top">
                  <span className={`test-ave-status test-ave-status--${statusClassName}`}>
                    {statusLabel}{percent !== null && !ready ? ` ${percent}%` : ''}
                  </span>
                  {requiresPassword && (
                    <span className="test-ave-lock">
                      <i className="fas fa-lock" />
                      Con clave
                    </span>
                  )}
                </div>
                <h2>{resolveAssistantName(assistant)}</h2>
                {(assistant.descripcion || assistant.Descripcion) && (
                  <p className="test-ave-card__description">
                    {assistant.descripcion || assistant.Descripcion}
                  </p>
                )}
                {detail && (
                  <p className="test-ave-card__detail">{detail}</p>
                )}
                <button
                  type="button"
                  className="test-ave-card__button"
                  onClick={() => handleOpenAssistant(assistant)}
                  disabled={!ready}
                >
                  <i className="fas fa-comments" />
                  {ready ? 'Probar chat' : 'No disponible'}
                </button>
              </article>
            );
          })}
        </section>
      </main>

      {showConsent && selectedAssistant && (
        <div className="consent-modal-backdrop" onClick={handleCancelConsent}>
          <div className="consent-modal" onClick={(event) => event.stopPropagation()}>
            <div className="consent-modal__header">
              <div className="consent-modal__icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <p className="consent-modal__eyebrow">Consentimiento previo</p>
              <h2 className="consent-modal__title">Antes de ingresar al asistente</h2>
            </div>

            <div className="consent-modal__body">
              <div className="consent-modal__content">
                <p className="consent-modal__lead">
                  A continuación accederás al asistente de la materia:
                </p>
                <p className="consent-modal__subject">
                  <i className="fas fa-book-open"></i>
                  <span>{selectedSubjectName}</span>
                </p>
                <p className="consent-modal__assistant">
                  <span>Asistente</span>
                  <strong>{selectedAssistantName}</strong>
                </p>
                <div className="consent-modal__notice">
                  <p>
                    Las respuestas generadas por este asistente tienen carácter orientativo y deben considerarse como apoyo al proceso de aprendizaje.
                  </p>
                  <p>
                    Se recomienda contrastar la información con las fuentes oficiales de la cátedra o con el equipo docente.
                  </p>
                </div>
                <p className="consent-modal__hint">
                  Si aceptás, se abrirá el chat público del asistente AVE.
                </p>
                {selectedRequiresPassword && (
                  <div className="consent-modal__field">
                    <label className="consent-modal__label">Password de acceso</label>
                    <input
                      type="password"
                      value={passwordInput}
                      onChange={(event) => setPasswordInput(event.target.value)}
                      placeholder="Ingresa el password del asistente"
                      className="consent-modal__input"
                    />
                    {passwordError && (
                      <p className="consent-modal__error">{passwordError}</p>
                    )}
                  </div>
                )}
                {!selectedRequiresPassword && passwordError && (
                  <p className="consent-modal__error">{passwordError}</p>
                )}
              </div>
            </div>

            <div className="consent-modal__actions">
              <button
                type="button"
                onClick={handleCancelConsent}
                className="consent-modal__button consent-modal__button--secondary"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmConsent}
                disabled={isValidatingPassword}
                className="consent-modal__button consent-modal__button--primary"
              >
                {isValidatingPassword ? 'Validando...' : 'Entendido, continuar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showChatModal && (
        <ChatModal
          isOpen={showChatModal}
          onClose={handleCloseChat}
          assistant={chatAssistant}
          publicChat
          regionalId={regionalId}
          areaId={areaId}
          password={chatPassword || null}
          headerTitle={chatTitle}
        />
      )}
    </div>
  );
};

export default TestAve;
